import { ref } from '@nuxtjs/composition-api';
import { useApi } from '@wemade-vsf/composables';
import { Logger } from '@wemade-vsf/core';
import getLicensePlatesGql from './getLicensePlates.gql';
import removeLicensePlateGql from './removeLicensePlate.gql';
import registerLicensePlateGql from './registerLicensePlate.gql';
import type { LicensePlate, LicensePlateInput, LicensePlateQuery } from './types';

export * from './types';

export function useLicensePlateRegistration() {
  const loading = ref<boolean>(false);
  const error = ref<{ load: Error | null; remove: Error | null }>({
    load: null,
    remove: null
  })

  const { query, mutate } = useApi();

  async function getRegisteredPlates(): Promise<LicensePlate[] | null>{
    Logger.debug('useLicensePlateRegistration', 'getRegisteredPlates');
    let result = null;
    try {
      loading.value = true;
      const { data } = await query<LicensePlateQuery>(getLicensePlatesGql);
      result = data?.wmLicensePlates ?? null;
      error.value.load = null;
      Logger.debug('useLicensePlateRegistration', 'getRegisteredPlates', result);
    } catch (err) {
      Logger.error(err);
      error.value.load = err;
    } finally {
      loading.value = false;
    }
    return result;
  }

  async function registerPlate(licensePlate: LicensePlateInput): Promise<LicensePlate | null> {
    Logger.debug('useLicensePlateRegistration', 'registerPlate', licensePlate);
    let result = null;
    try {
      const { data } = await mutate<{ wmLicensePlateRegister: LicensePlate }, { input: LicensePlateInput }>(registerLicensePlateGql, { input: licensePlate });
      result = data?.wmLicensePlateRegister ?? null;
      error.value.remove = null;
    } catch (err) {
      Logger.error(err);
      error.value.remove = err;
    }
    return result;
  }

  async function removePlate(uid: string): Promise<boolean> {
    let result = false;
    try {
      const { data } = await mutate<{ wmLicensePlateRemove: boolean }, { uid: string }>(removeLicensePlateGql, { uid });
      result = data?.wmLicensePlateRemove ?? false;
      error.value.remove = null;
    } catch (err) {
      Logger.error(err);
      error.value.remove = err;
    }
    return result;
  }

  return {
    loading,
    error,
    getRegisteredPlates,
    registerPlate,
    removePlate
  }
}